const { fetchJSON } = window.BAO.Utils

export class StoreSelector extends window.BAO.CustomElement() {
  /*
    Test mode will show store selector, even if localStorage exists.
    Test mode skips geoip request and sets country code to ES (Spain).
  */
  testMode = false

  connectedCallback () {
    super.connectedCallback()

    if (this.storedUserLocation?.length) {
      document.body.setAttribute('data-country', this.storedUserLocation)
    }

    // eslint-disable-next-line compat/compat
    const params = new Proxy(new URLSearchParams(window.location.search), {
      get: (searchParams, prop) => searchParams.get(prop),
    })
    const countryParam = params.country

    if (window.location.href.indexOf('#geo') > -1) {
      return this.forceRedirect()
    }

    if (
      !this.noChoiceMade &&
      !this.noLocationStored &&
      countryParam == null &&
      this.testMode === false
    ) {
      return
    }

    if (countryParam !== null) {
      this.storedUserLocation = countryParam
      return
    }

    if (this.testMode) {
      this.setupCountrySwitcherModal('ES')
    } else {
      fetchJSON(`/browsing_context_suggestions.json`, {
        method: 'GET',
      }).then(res => {
        this.setupCountrySwitcherModal(res.detected_values.country.handle)
      })
    }
  }

  forceRedirect () {
    fetchJSON(`/browsing_context_suggestions.json`, {
      method: 'GET',
    }).then(res => {
      const userCountry = res.detected_values.country.handle

      window.location.href = `${
        this.sites[this.countries[userCountry].site].url
      }${window.location.pathname}?country=${userCountry}${
        this.sites[this.countries[userCountry].site].currency
      }`
    })
  }

  setupListeners () {
    super.setupListeners()

    this.onOptionClick = this.onOptionClick.bind(this)
    this.onConfirmClick = this.onConfirmClick.bind(this)
    this.onCloseClick = this.onCloseClick.bind(this)

    if (this.els?.options.exists) {
      this.els.options.elements.forEach(optionEl => {
        this.listeners.add(optionEl, 'change', this.onOptionClick)
      })
    }

    if (this.els?.confirm.exists) {
      this.els.confirm.elements.forEach(confirmEl => {
        this.listeners.add(confirmEl, 'click', this.onConfirmClick)
      })
    }

    if (this.els?.close?.exists) {
      this.els.close.elements.forEach(closeEL => {
        this.listeners.add(closeEL, 'click', this.onCloseClick)
      })
    }
  }

  onOptionClick (e) {
    const selectedOptionValue = e.currentTarget.value
    const selectedOptionCode =
      e.currentTarget.options[e.currentTarget.selectedIndex].dataset.code

    this.els.confirm.elements.forEach(confirmEl => {
      confirmEl.dataset.url = this.sites[selectedOptionValue].url
      confirmEl.dataset.code = selectedOptionCode
      confirmEl.dataset.currency = this.sites[selectedOptionValue].currency
    })
  }

  onConfirmClick (event) {
    event.preventDefault()

    this.storedUserLocation = event.currentTarget.dataset.code

    if (event.currentTarget.hasAttribute('data-url')) {
      window.location.href = `${event.currentTarget.dataset.url}${window.location.pathname}?country=${event.currentTarget.dataset.code}${event.currentTarget.dataset.currency}`
    }
  }

  onCloseClick (event) {
    event.preventDefault()

    this.storedUserLocation = this.dataset.store

    this.setAttribute('aria-hidden', 'true')

    if (document.body.classList.contains('util-DrawersOpen')) return
    document.body.classList.remove('util-SiteOverflowed')
  }

  setupCountrySwitcherModal (countryCode) {
    if (countryCode === this.dataset.store) {
      this.storedUserLocation = countryCode
      return
    }

    this.createOptionsList(countryCode)
    this.setDefaultConfirmButtonURL()
    this.setAttribute('aria-hidden', 'false')
    document.body.classList.add('util-SiteOverflowed')
  }

  createOptionsList (countryCode) {
    Object.entries(this.countries).forEach(([key, value]) => {
      const { name, site } = value
      const isCurrentSite = key.toLowerCase() === countryCode.toLowerCase()
      this.els.options.element.innerHTML += this.createOptionElement(
        key,
        site,
        isCurrentSite,
        name,
      )
    })
  }

  createOptionElement (code, site, isCurrentSite, country) {
    return `<option
      data-code="${code}"
      value="${site}"
      ${isCurrentSite ? 'selected' : ''}
    >
      ${country}
    </option>`
  }

  setDefaultConfirmButtonURL () {
    const selectedOptionValue = this.els.options.element.value
    const selectedOptionCode = this.els.options.element.options[
      this.els.options.element.selectedIndex
    ].dataset.code

    this.els.confirm.elements.forEach(confirmEl => {
      confirmEl.dataset.url = this.sites[selectedOptionValue].url
      confirmEl.dataset.code = selectedOptionCode
      confirmEl.dataset.currency = this.sites[selectedOptionValue].currency
    })
  }

  set storedUserLocation (countryCode) {
    window.localStorage.setItem(this.storageName, 'true')
    window.localStorage.setItem(
      this.userLocationName,
      countryCode.toUpperCase(),
    )
    document.body.setAttribute('data-country', countryCode.toUpperCase())
  }

  get storedUserLocation () {
    return window.localStorage.getItem(this.userLocationName)
  }

  get currentCountryList () {
    return this.getAttribute('switcher-country-list')
  }

  get noChoiceMade () {
    return (
      window.localStorage.getItem(this.storageName) == null ||
      (window.localStorage.getItem(this.storageName) === undefined &&
        !Shopify.designMode)
    )
  }

  get noLocationStored () {
    return (
      !this.storedUserLocation?.length ||
      (!this.storedUserLocation?.length && !Shopify.designMode)
    )
  }

  get storageName () {
    return `BAO-store-sunspel`
  }

  get userLocationName () {
    return `BAO-location-sunspel`
  }

  get sites () {
    return {
      UK: {
        url: 'https://sunspel.myshopify.com',
        currency: '',
      },
      US: { url: 'https://sunspel-us.myshopify.com', currency: '' },
      DE: { url: 'https://sunspel-de.myshopify.com', currency: '' },
      FR: { url: 'https://sunspel-fr.myshopify.com', currency: '' },
      EU: { url: 'https://sunspel-eu.myshopify.com', currency: '' },
      JP: { url: 'https://sunspel-jp.myshopify.com', currency: '' },
    }
  }

  get countries () {
    return window.theme.countries
  }
}

if (!customElements.get('store-selector')) {
  customElements.define('store-selector', StoreSelector)
}
